/* Dashboard AI chat — SSE-streamelt kérdés-válasz (D-WP-06).
 *
 * Miért `fetch()`+POST, nem `EventSource`: az `EventSource` csak GET-et tud,
 * és a kérdés szövegét URL-be/szerver-logba tenni ugyanaz az adat-kiszivárgási
 * osztály, amit a `posthog-analitika.md` az összeg/IBAN eseményekre tilt —
 * itt a teljes kérdés-szöveg, ami legalább annyira érzékeny.
 *
 * A backend `data: {...}\n\n` keretekben küldi az eseményeket
 * (`backend/app/api/chat.py` `_sse()`); ez a függvény ezt bontja vissza.
 */
import { api, COMPANY_HEADER, getStoredCompanyId } from './api';

/* ⚠⚠ 2026-09-05 — MINDEN hívás a megosztott `services/api.js` axios-kliensen megy.
 *
 * Korábban ez a fájl nyers `fetch()`-eket használt, SZÁNDÉKOSAN kerülve az
 * `api.js`-t (hogy ne kelljen hozzányúlni egy párhuzamos munkamenet által
 * szerkesztett fájlhoz). Az M1 (2026-09-04) viszont az aktív cég
 * `X-Company-Id` fejlécét CSAK abba az axios-kliensbe tette be, így ezek a
 * hívások fejléc NÉLKÜL mentek, a backend pedig a csendes
 * `memberships[0]`-fallbackre esett (`core/database.py` `_resolve_company_id`).
 * Élő következmény (Gábor Telegram-próbája): többcéges usernél a Chat
 * beállítások és a Dashboard-chat a ROSSZ cég adatát mutatta/írta — a
 * `/telegram/nonce` a rossz céghez állított ki linktokent, a
 * `/chat/delete-history` a rossz cég előzményét törölte volna.
 *
 * > **Ne indíts új API-hívást nyers `fetch()`-csel.** Reason: a cég-fejlécet
 * > (és a 401-refresh + diagnosztika interceptorokat) csak az `api.js` kliens
 * > teszi fel, a hiánya pedig NÉMA — rossz cég adatát adja, nem hibát.
 * > Instead: `api.get/post/patch`; ha tényleg kell nyers `fetch` (SSE-stream),
 * > a fejlécet kézzel tedd fel — lásd `askChatStream()`.
 *
 * A hibák alakja VÁLTOZATLAN (`err.message` = a backend `detail`, `err.status`
 * = HTTP-státusz), mert a hívók erre építenek (pl. `ChatWidget.js` 429-ága). */
function _rethrow(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  const err = new Error(detail || (status ? `HTTP ${status}` : e?.message) || 'Ismeretlen hiba');
  err.status = status;
  throw err;
}

const _call = (fn) => fn().then(r => r.data).catch(_rethrow);

/* D-WP-08 — a mai kvóta-fogyasztás lekérdezése a "Chat settings" fülnek. */
export const getChatQuota = () => _call(() => api.get('/chat/quota'));
// E4 (2026-09-06): chat-költség mérés (lista-áras becslés, modellenként)
export const getChatCost = (days = 30) => _call(() => api.get('/chat/cost', { params: { days } }));

/* Telegram binding-életciklus (WP-02). */
function _chatSettingsJson(path, { method = 'GET', body } = {}) {
  const url = `/chat-settings${path}`;
  if (method === 'GET') return _call(() => api.get(url));
  if (method === 'PATCH') return _call(() => api.patch(url, body));
  return _call(() => api.post(url, body));
}

export const issueTelegramNonce = () => _chatSettingsJson('/telegram/nonce', { method: 'POST' });
export const getTelegramPending = () => _chatSettingsJson('/telegram/pending');
export const confirmTelegramPending = (id) => _chatSettingsJson(`/telegram/pending/${id}/confirm`, { method: 'POST' });
export const disownTelegramPending = (id) => _chatSettingsJson(`/telegram/pending/${id}/disown`, { method: 'POST' });
export const getTelegramBindings = () => _chatSettingsJson('/telegram/bindings');
export const revokeTelegramBinding = (id) => _chatSettingsJson(`/telegram/bindings/${id}/revoke`, { method: 'POST' });
// WP-09 — a Beállítások-panel bővítése (érzékenység-kapcsoló, csendes óra, teszt-üzenet).
export const updateTelegramBinding = (id, fields) =>
  _chatSettingsJson(`/telegram/bindings/${id}`, { method: 'PATCH', body: fields });
export const sendTelegramTestMessage = (id) =>
  _chatSettingsJson(`/telegram/bindings/${id}/test-message`, { method: 'POST' });

/* WP-09 privacy-panel — "töröld az előzményemet most". */
export const deleteChatHistory = () => _call(() => api.post('/chat/delete-history'));

export async function askChatStream(question, { onEvent, signal } = {}) {
  // ⚠ Nyers `fetch()` — az axios nem tud választörzset streamelni. Ezért az
  //   `api.js` interceptora NEM fut le, és a cég-fejlécet KÉZZEL tesszük fel:
  //   enélkül a chat a `memberships[0]`-fallback cég adataiból válaszolna.
  const companyId = getStoredCompanyId();
  const res = await fetch('/api/chat/ask/stream', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(companyId != null ? { [COMPANY_HEADER]: String(companyId) } : {}),
    },
    body: JSON.stringify({ question }),
    signal,
  });
  if (!res.ok) {
    let detail = null;
    try { detail = (await res.json()).detail; } catch { /* nem JSON válasz */ }
    const err = new Error(detail || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      onEvent(JSON.parse(line.slice(6)));
    }
  }
}
